import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  keepPreviousData,
  type InfiniteData,
} from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import {
  acceptSurfaceAsset,
  bulkSurfaceAction,
  fetchSurfaceFeed,
  fetchSurfaceFilterTags,
  fetchSurfaceProgress,
  fetchSurfaceSolutionTags,
  reanalyzePendingSurface,
  rejectSurfaceAsset,
  reprocessSurfaceAsset,
  skipSurfaceAsset,
} from "./promoteSurface.service";
import { promoteSurfaceKeys } from "./promoteSurface.query-keys";
import type {
  AcceptSurfaceAssetBody,
  ReanalyzePendingBody,
  SurfaceBulkBody,
  SurfaceFeedFilters,
  SurfaceFeedResponse,
  SurfaceProgress,
  SurfaceTab,
  SurfaceWriteAction,
} from "./types";
import { EMPTY_SURFACE_FEED_FILTERS, verificationStatusForTab } from "./types";
import { invalidateMarketingAssets } from "@/react/components/entityViews/marketing/api/marketingAssets.cache";

const CONCURRENCY = 8;

function setProgressCache(slug: string, progress: SurfaceProgress | undefined) {
  if (!progress) return;
  queryClient.setQueryData(promoteSurfaceKeys.progress(slug), progress);
}

async function invalidateSurfaceCaches(slug: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: promoteSurfaceKeys.board(slug) }),
  ]);
}

export { invalidateSurfaceCaches };

export function useSurfaceProgressQuery(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: promoteSurfaceKeys.progress(slug ?? ""),
    queryFn: () => fetchSurfaceProgress(slug!),
    enabled: enabled && !!slug,
    refetchInterval: 1000,
    refetchIntervalInBackground: false,
  });
}

export function useSurfaceFeedInfiniteQuery(
  slug: string | undefined,
  tab: SurfaceTab,
  enabled = true,
  filters: SurfaceFeedFilters = EMPTY_SURFACE_FEED_FILTERS
) {
  return useInfiniteQuery({
    queryKey: promoteSurfaceKeys.feed(slug ?? "", tab, filters),
    initialPageParam: undefined as string | number | undefined,
    queryFn: async ({ pageParam }) => {
      const page = await fetchSurfaceFeed(slug!, {
        verificationStatus: verificationStatusForTab(tab),
        cursor: pageParam,
        tagIds: filters.tagIds,
        excludeTagIds: filters.excludeTagIds,
        taskId: filters.taskId,
      });
      setProgressCache(slug!, page.progress);
      return page;
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled: enabled && !!slug,
    placeholderData: keepPreviousData,
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
  });
}

export function useSurfaceSolutionTagsQuery(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: promoteSurfaceKeys.solutionTags(slug ?? ""),
    queryFn: () => fetchSurfaceSolutionTags(slug!),
    enabled: enabled && !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSurfaceFilterTagsQuery(slug: string | undefined, enabled = true) {
  return useQuery({
    queryKey: promoteSurfaceKeys.filterTags(slug ?? ""),
    queryFn: () => fetchSurfaceFilterTags(slug!),
    enabled: enabled && !!slug,
    staleTime: 5 * 60 * 1000,
  });
}

export function flattenFeedAssets(
  data: InfiniteData<SurfaceFeedResponse> | undefined
) {
  return data?.pages.flatMap((p) => p.assets) ?? [];
}

function firstFieldError(data: Record<string, unknown> | undefined): string | null {
  if (!data) return null;
  for (const key of ["tags", "quota", "verification_status", "detail", "message"]) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) return value;
    if (Array.isArray(value) && value.length) {
      const first = value[0];
      if (typeof first === "string") return first;
    }
  }
  return null;
}

function getAxiosMessage(error: unknown): string {
  const err = error as {
    response?: { data?: Record<string, unknown>; status?: number };
    message?: string;
  };
  const fromFields = firstFieldError(err?.response?.data);
  if (fromFields) return fromFields;
  return err?.message || "Request failed";
}

type SurfaceWritePayload = {
  action: SurfaceWriteAction;
  assetId: number;
  body?: AcceptSurfaceAssetBody;
};

/** Single write path for live single and bulk fan-out. */
async function executeSurfaceWrite(slug: string, input: SurfaceWritePayload) {
  switch (input.action) {
    case "accept":
      return acceptSurfaceAsset(slug, input.assetId, input.body ?? { tag_ids: [] });
    case "reject":
      return rejectSurfaceAsset(slug, input.assetId);
    case "skip":
      return skipSurfaceAsset(slug, input.assetId);
    case "reprocess":
      return reprocessSurfaceAsset(slug, input.assetId);
  }
}

export function useSurfaceAssetMutations(
  slug: string | undefined,
  boardId?: number | null,
) {
  const runSingle = useMutation({
    mutationFn: async (input: {
      action: SurfaceWriteAction;
      assetId: number;
      body?: AcceptSurfaceAssetBody;
      skipInvalidate?: boolean;
    }) => {
      if (!slug) throw new Error("Missing board slug");
      return executeSurfaceWrite(slug, input);
    },
    onSuccess: async (_data, variables) => {
      if (variables.skipInvalidate) return;
      if (slug) {
        await invalidateSurfaceCaches(slug);
      }
      if (
        boardId != null &&
        (variables.action === "accept" ||
          variables.action === "reject" ||
          variables.action === "skip")
      ) {
        await invalidateMarketingAssets(boardId);
      }
    },
  });

  const runBulk = useMutation({
    mutationFn: async (input: SurfaceBulkBody) => {
      if (!slug) throw new Error("Missing board slug");
      if (input.use_filter) {
        return bulkSurfaceAction(slug, input);
      }
      try {
        return await bulkSurfaceAction(slug, {
          ...input,
          asset_ids: input.asset_ids ?? [],
        });
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        // Only fan out when the bulk route is missing; rethrow auth/validation errors.
        if (status === 404 || status === 405) {
          return fanOutBulk(slug, input);
        }
        throw error;
      }
    },
    onSuccess: async (_data, variables) => {
      if (variables.preview) return;
      if (slug) await invalidateSurfaceCaches(slug);
      if (
        boardId != null &&
        (variables.action === "accept" ||
          variables.action === "reject" ||
          variables.action === "skip")
      ) {
        await invalidateMarketingAssets(boardId);
      }
    },
  });

  /** Per-asset accepts (different tag_ids) with shared invalidate — used by multi bulk accept. */
  const runFanOut = useMutation({
    mutationFn: async (writes: SurfaceWritePayload[]) => {
      if (!slug) throw new Error("Missing board slug");
      return fanOutWrites(slug, writes);
    },
    onSuccess: async (_data, writes) => {
      if (slug) await invalidateSurfaceCaches(slug);
      if (
        boardId != null &&
        writes.some(
          (w) =>
            w.action === "accept" || w.action === "reject" || w.action === "skip"
        )
      ) {
        await invalidateMarketingAssets(boardId);
      }
    },
  });

  const reanalyzePending = useMutation({
    mutationFn: async (body: ReanalyzePendingBody = { async: true }) => {
      if (!slug) throw new Error("Missing board slug");
      return reanalyzePendingSurface(slug, body);
    },
    onSuccess: async () => {
      if (slug) await invalidateSurfaceCaches(slug);
    },
  });

  return {
    runSingle,
    runBulk,
    runFanOut,
    reanalyzePending,
    getAxiosMessage,
    invalidateSurfaceCaches: () => slug && invalidateSurfaceCaches(slug),
  };
}

async function fanOutWrites(slug: string, writes: SurfaceWritePayload[]) {
  const results: Array<{
    asset_id: number;
    ok: boolean;
    error?: string;
  }> = [];

  for (let i = 0; i < writes.length; i += CONCURRENCY) {
    const chunk = writes.slice(i, i + CONCURRENCY);
    const settled = await Promise.allSettled(
      chunk.map((write) =>
        executeSurfaceWrite(slug, write).then(() => write.assetId)
      )
    );

    settled.forEach((result, idx) => {
      const assetId = chunk[idx].assetId;
      if (result.status === "fulfilled") {
        results.push({ asset_id: assetId, ok: true });
      } else {
        results.push({
          asset_id: assetId,
          ok: false,
          error: getAxiosMessage(result.reason),
        });
      }
    });
  }

  return {
    results,
    mode: "sync" as const,
    count: results.length,
    action: writes[0]?.action,
  };
}

async function fanOutBulk(slug: string, body: SurfaceBulkBody) {
  const ids = body.asset_ids ?? [];
  return fanOutWrites(
    slug,
    ids.map((assetId) => ({
      action: body.action,
      assetId,
      body: {
        tag_ids: body.tag_ids ?? [],
        caption: body.caption,
        alt_text: body.alt_text,
        is_restricted: body.is_restricted,
        restriction_reason: body.restriction_reason,
      },
    }))
  );
}

export { getAxiosMessage };
